/**
 * Blog posts merged into another URL, answered with a 301.
 *
 * The daily cron wrote many topics several times under slightly different
 * titles (seven articles on interview body language, nine on AI in hiring),
 * and some articles competed with a guide page for the same query. Each group
 * below keeps one URL and points the rest at it, so the ranking signals the
 * duplicates collected land on a single page.
 *
 * Which one is kept: the longest article, unless another is close in length and
 * has a slug that reads as the search query itself. Where a guide page or
 * a tool page owns the query, the whole group goes there instead.
 *
 * The merged posts are also unpublished in the database
 * (supabase/migrations/*_blog_merge_*.sql, one file per batch) so they leave
 * the listing and the sitemap. tests/constants/blogRedirects.test.ts keeps
 * those files and this list in step.
 */
interface RedirectGroup {
  /** Path the group redirects to: a kept blog post or a guide page. */
  to: string
  /** Slugs under /blog/ that stop existing. */
  from: string[]
}

const post = (slug: string) => `/blog/${slug}`

export const BLOG_REDIRECT_GROUPS: RedirectGroup[] = [
  // ── Owned by guide pages ─────────────────────────────────────
  {
    to: '/how-to-prepare-for-an-interview',
    from: [
      'acing-your-job-interview-expert-tips-and-strategies',
      'mastering-your-job-interview-expert-tips-and-strategies',
      'how-to-ace-your-job-interview-in-24-hours-an-emergency-guide',
      'how-to-prepare-for-a-job-interview-in-24-hours-an-emergency-guide',
      'how-to-prepare-for-a-job-interview-in-24-hours-emergency-guide',
      'top-20-job-interview-questions-and-how-to-answer-them-like-a-pro',
      'online-it-interview-preparation-junior-and-senior-roles',
    ],
  },
  {
    to: '/how-to-write-a-resume',
    from: [
      'best-practices-for-writing-a-resume-that-gets-you-hired-2',
      'how-to-write-a-perfect-resume-in-2026-a-complete-step-by-step-guide',
    ],
  },
  {
    to: '/how-long-should-a-resume-be',
    from: ['one-page-resume-or-two-deciding-the-perfect-length-for-your-cv'],
  },
  {
    to: '/ai-cover-letter',
    from: [
      'how-to-write-a-cover-letter-that-actually-gets-read-in-2026',
      'the-power-of-a-cover-letter-boost-your-resume-with-inmyresume',
    ],
  },
  // Short product posts Google crawled and declined to index (Search Console,
  // 2026-10-15); the feature page says the same thing better.
  {
    to: '/online-cv',
    from: [
      'taking-your-cv-to-the-digital-age-shareable-online-resumes-with-inmyresume',
      'crafting-a-public-cv-with-inmyresume-your-gateway-to-global-opportunities',
      'resumes-why-you-should-share-your-cv-online',
    ],
  },
  {
    to: '/tailor-resume-to-job',
    from: ['the-power-of-a-tailored-resume-why-you-should-have-a-different-cv-for-every-job'],
  },
  {
    to: '/cv-analytics',
    from: ['unlocking-job-search-insights-cv-analytics-in-inmyresume'],
  },
  {
    to: '/templates',
    from: ['why-your-resume-presentation-matters-and-how-inmyresumecom-can-help'],
  },

  // ── Owned by /resume-examples/<slug> ─────────────────────────
  // One group per example page, added as the page is built
  // (supabase/migrations/*_blog_merge_resume_examples*.sql).
  {
    to: '/resume-examples/teacher',
    from: ['teacher-resume-example-best-proven-guide-for-2025'],
  },
  {
    to: '/resume-examples/special-education-teacher',
    from: ['special-education-teacher-resume-complete-guide-example-for-2025'],
  },
  {
    to: '/resume-examples/software-engineer',
    from: ['how-to-write-a-tech-resume-that-gets-you-interviews-at-top-companies'],
  },
  {
    to: '/resume-examples/marketing',
    from: ['marketing-resume-how-to-showcase-your-results-and-creativity'],
  },
  {
    to: '/resume-examples/sales',
    from: ['sales-resume-how-to-quantify-your-achievements-and-close-the-deal'],
  },
  {
    to: '/resume-examples/accountant',
    from: ['finance-and-accounting-resume-numbers-that-prove-your-value'],
  },
  {
    to: '/resume-examples/project-manager',
    from: ['project-manager-resume-showcasing-leadership-and-results'],
  },

  // ── Job interview ────────────────────────────────────────────
  {
    to: post('mastering-body-language-in-job-interviews-decoding-your-posture'),
    from: [
      'mastering-body-language-in-job-interviews-what-your-posture-says-about-you',
      'mastering-body-language-in-job-interviews-what-your-posture-says-about-you-1',
      'mastering-body-language-in-job-interviews-what-your-posture-says-about-you-2',
      'mastering-body-language-in-job-interviews-what-your-posture-says-about-you-3',
      'mastering-body-language-in-job-interviews-what-your-posture-says-about-you-4',
      'mastering-body-language-in-job-interviews-what-your-posture-says-about-you-5',
      'mastering-body-language-in-job-interviews-unlocking-the-power-of-posture',
      'mastering-body-language-in-job-interviews-what-your-posture-says',
    ],
  },
  {
    to: post('mastering-the-tell-me-about-yourself-question-in-job-interviews'),
    from: [
      'mastering-the-tell-me-about-yourself-interview-question-a-comprehensive-guide',
      'mastering-the-tell-me-about-yourself-interview-question-a-comprehensive-guide-1',
    ],
  },
  {
    to: post('mastering-remote-job-interviews-key-differences-and-preparation-tips'),
    from: [
      'mastering-the-remote-job-interview-key-differences-and-preparation-tips',
      'mastering-the-remote-job-interview-key-differences-and-preparation-tips-1',
    ],
  },
  {
    to: post('harnessing-chatgpt-for-job-interview-success-a-practical-guide'),
    from: [
      'harnessing-chatgpt-your-ultimate-guide-to-job-interview-preparation',
      'harnessing-chatgpt-the-ultimate-job-interview-preparation-tool',
      'harnessing-chatgpt-for-job-interview-success-a-comprehensive-guide',
      'mastering-job-interviews-how-to-use-chatgpt-as-your-secret-weapon',
    ],
  },

  // ── Resume & CV ──────────────────────────────────────────────
  {
    to: post('how-to-create-an-ats-friendly-resume-get-past-applicant-tracking-systems'),
    from: [
      'unlocking-the-ats-code-how-to-craft-an-ats-friendly-resume',
      'mastering-the-ats-friendly-resume-a-key-to-job-application-success',
      'mastering-the-ats-friendly-resume-how-to-get-past-applicant-tracking-systems',
      'mastering-the-ats-friendly-resume-navigate-applicant-tracking-systems-successfully',
      'beat-the-bots-how-to-create-an-ats-friendly-resume',
      'master-the-ats-how-ai-powered-systems-screen-your-resume-and-how-to-beat-them',
      'how-to-beat-ai-powered-ats-systems-a-guide-to-getting-your-resume-noticed',
    ],
  },
  {
    to: post('crafting-a-professional-summary-for-your-resume-that-stands-out'),
    from: [
      'crafting-a-professional-summary-for-your-resume-that-stands-out-1',
      'crafting-a-professional-summary-for-your-resume-that-stands-out-2',
      'crafting-a-professional-summary-make-your-resume-stand-out',
    ],
  },
  {
    to: post('how-to-list-skills-on-your-resume-a-comprehensive-guide-to-hard-and-soft-skills'),
    from: [
      'how-to-list-skills-on-your-resume-a-comprehensive-guide-to-hard-and-soft-skills-1',
      'mastering-resume-skills-a-comprehensive-guide-to-hard-and-soft-skills',
      'mastering-resume-skills-a-comprehensive-guide-to-hard-skills-vs-soft-skills',
      'mastering-your-resume-an-essential-guide-to-hard-and-soft-skills',
    ],
  },
  {
    to: post('10-resume-mistakes-that-cost-you-job-interviews-and-how-to-avoid-them'),
    from: [
      '10-common-resume-mistakes-that-cost-you-job-interviews',
      '10-resume-mistakes-that-cost-you-job-interviews-and-how-to-fix-them',
    ],
  },
  {
    // The resume guide links to this one.
    to: post('resume-vs-cv-understanding-the-differences-and-when-to-use-each'),
    from: ['resume-vs-cv-understanding-the-difference-and-when-to-use-each'],
  },
  {
    // The resume guide links to this one.
    to: post('how-to-write-your-first-resume-with-no-work-experience'),
    from: ['crafting-your-first-resume-without-work-experience-a-step-by-step-guide'],
  },
  {
    to: post('resume-trends-2025-what-recruiters-want-to-see-this-year'),
    from: ['reshaping-your-resume-2025-trends-to-watch-out-for'],
  },

  // ── Cover letter ─────────────────────────────────────────────
  {
    to: post('cover-letter-vs-no-cover-letter-do-employers-still-care'),
    from: ['cover-letter-vs-no-cover-letter-do-employers-still-care-1'],
  },
  {
    to: post('how-to-write-a-cover-letter-with-no-experience-fresh-graduate-guide'),
    from: ['how-to-write-a-compelling-cover-letter-with-no-experience-a-fresh-graduates-guide'],
  },
  {
    to: post('10-cover-letter-mistakes-that-can-cost-you-the-job'),
    from: ['cover-letter-mistakes-that-immediately-disqualify-you-from-consideration'],
  },

  // ── Industry-specific resumes ────────────────────────────────
  // The articles these used to point at redirect to /resume-examples now, so
  // the duplicates go straight there rather than through a second 301.
  {
    to: '/resume-examples/project-manager',
    from: [
      'crafting-a-project-manager-resume-showcase-leadership-and-results',
      'crafting-a-project-manager-resume-highlighting-leadership-and-achievements',
      'project-manager-resume-demonstrating-leadership-and-results',
      'project-manager-resume-showcasing-leadership-and-results-for-career-success',
      'project-manager-resume-showcase-leadership-and-results',
      'how-to-showcase-leadership-and-achievements-in-your-project-manager-resume',
    ],
  },
  {
    to: post('designer-portfolio-and-resume-showcase-your-visual-skills-effectively'),
    from: [
      'crafting-a-designer-portfolio-and-resume-showcasing-your-visual-skills',
      'designer-portfolio-and-resume-mastering-the-art-of-showcasing-your-visual-skills',
      'designer-portfolio-and-resume-showcasing-your-visual-skills-effectively',
      'how-to-showcase-your-visual-skills-designer-portfolio-and-resume-tips',
    ],
  },
  {
    to: '/resume-examples/marketing',
    from: ['showcasing-results-and-creativity-in-your-marketing-resume'],
  },
  {
    to: '/resume-examples/sales',
    from: ['quantify-your-sales-achievements-how-to-close-the-deal-with-your-resume'],
  },

  // ── Remote work ──────────────────────────────────────────────
  {
    to: post('how-to-highlight-your-remote-ready-skills-on-a-resume'),
    from: [
      'mastering-the-remote-work-resume-showcase-your-remote-ready-skills',
      'remote-work-resume-highlight-your-remote-ready-skills-effectively',
      'highlighting-your-remote-ready-skills-on-your-resume-a-comprehensive-guide',
      'crafting-a-remote-work-resume-highlighting-your-remote-ready-skills',
    ],
  },
  {
    to: post('top-countries-for-digital-nomads-and-remote-workers-in-2026'),
    from: [
      'top-countries-for-digital-nomads-and-remote-workers-in-2026-1',
      'top-countries-for-digital-nomads-and-remote-workers-in-2026-2',
      'top-destinations-for-digital-nomads-and-remote-workers-in-2026',
    ],
  },
  {
    to: post('thriving-in-the-hybrid-work-model-strategies-for-success'),
    from: ['thriving-in-the-hybrid-work-model-essential-tips-for-success'],
  },

  // ── Students & graduates ─────────────────────────────────────
  {
    to: post('from-college-to-career-a-graduate-survival-guide-for-the-job-market'),
    from: [
      'from-college-to-career-a-comprehensive-graduate-survival-guide',
      'from-college-to-career-a-graduates-survival-guide-for-the-job-market',
    ],
  },
  {
    to: post('how-to-land-an-entry-level-job-without-experience-strategies-for-success'),
    from: [
      'how-to-compete-for-entry-level-jobs-winning-without-experience',
      'how-to-compete-for-entry-level-jobs-when-everyone-wants-experience',
      'entry-level-jobs-how-to-compete-when-everyone-wants-experience',
    ],
  },
  {
    to: post('maximizing-university-projects-and-volunteering-on-your-resume'),
    from: [
      'maximizing-university-projects-and-volunteering-on-your-resume-1',
      'maximize-your-resume-leveraging-university-projects-and-volunteering',
    ],
  },
  {
    to: post('top-student-side-hustles-that-build-your-resume-and-career-skills'),
    from: [
      'student-side-hustles-that-build-your-resume-and-career-skills',
      'student-side-hustles-that-actually-build-your-resume-and-career-skills',
    ],
  },
  {
    to: post('internship-guide-how-to-find-apply-and-make-the-most-of-it'),
    from: ['internship-guide-how-to-find-apply-and-maximize-your-experience'],
  },

  // ── AI & technology ──────────────────────────────────────────
  {
    to: post('how-ai-is-transforming-the-hiring-process-in-2026-a-guide-for-job-seekers'),
    from: [
      'how-ai-is-revolutionizing-the-hiring-process-in-2026-essential-insights-for-candidates',
      'how-ai-is-revolutionizing-the-hiring-process-in-2026-essential-insights-for-candidates-1',
      'how-ai-is-revolutionizing-the-hiring-process-in-2026-essential-insights-for-job-seekers',
      'how-ai-is-revolutionizing-the-hiring-process-in-2026-essential-insights-for-job-seekers-1',
      'how-ai-is-revolutionizing-the-hiring-process-in-2026-key-insights-for-job-seekers',
      'how-ai-is-transforming-the-hiring-process-in-2026-essential-insights-for-job-seekers',
      'how-ai-is-revolutionizing-hiring-in-2026-essential-insights-for-job-seekers',
      'how-ai-is-revolutionizing-hiring-in-2026-what-you-need-to-know',
    ],
  },
  {
    // The slug that names the product category people search for.
    to: post('ai-resume-builders-can-artificial-intelligence-write-your-resume'),
    from: ['harnessing-ai-can-artificial-intelligence-write-your-resume'],
  },

  // ── Career development ───────────────────────────────────────
  {
    to: post('top-professional-certifications-worth-getting-in-2026'),
    from: [
      'top-professional-certifications-worth-getting-in-2026-1',
      'top-professional-certifications-worth-getting-in-2026-2',
      'top-professional-certifications-worth-getting-in-2026-3',
      'top-professional-certifications-worth-pursuing-in-2026',
      'essential-professional-certifications-to-boost-your-career-in-2026',
      'professional-certifications-worth-getting-in-2026-boost-your-career-prospects',
      'professional-certifications-worth-getting-in-2026-enhance-your-career-prospects',
    ],
  },
  {
    to: post('top-10-soft-skills-employers-are-seeking-in-2026'),
    from: ['top-10-soft-skills-employers-will-demand-in-2026'],
  },
  {
    to: post('how-to-ask-for-a-promotion-timing-preparation-and-the-right-words'),
    from: ['mastering-the-art-of-asking-for-a-promotion-timing-preparation-and-the-right-words'],
  },
  {
    to: post('upskilling-vs-reskilling-how-to-future-proof-your-career'),
    from: ['upskilling-vs-reskilling-future-proof-your-career'],
  },
  {
    to: post('work-life-balance-myths-what-actually-works-for-career-satisfaction'),
    from: ['debunking-work-life-balance-myths-what-truly-leads-to-career-satisfaction'],
  },

  // ── Job search ───────────────────────────────────────────────
  {
    to: post('unlocking-the-hidden-job-market-discover-opportunities-beyond-job-boards'),
    from: [
      'the-hidden-job-market-unlocking-opportunities-beyond-advertised-positions',
      'unlocking-the-hidden-job-market-find-unadvertised-jobs',
      'unlocking-the-hidden-job-market-discover-unadvertised-opportunities',
      'unlocking-the-hidden-job-market-discover-jobs-never-advertised',
    ],
  },
  {
    to: post('job-search-strategy-2026-how-to-find-a-job-faster-in-the-modern-market'),
    from: ['job-search-strategy-2026-find-your-next-job-faster-in-the-modern-market'],
  },
  {
    to: post('cold-emailing-for-jobs-effective-templates-that-get-responses'),
    from: ['mastering-cold-emailing-for-jobs-templates-that-actually-get-responses'],
  },
  {
    to: post('mastering-job-boards-strategies-beyond-the-apply-button'),
    from: ['mastering-job-boards-strategies-beyond-just-clicking-apply'],
  },
  {
    to: post('career-change-at-30-40-or-50-its-never-too-late-to-switch-careers'),
    from: ['career-change-at-30-40-or-50-embrace-the-possibilities'],
  },

  // ── LinkedIn ─────────────────────────────────────────────────
  {
    to: post('how-to-write-a-linkedin-headline-that-gets-clicks'),
    from: [
      'how-to-write-a-linkedin-headline-that-draws-clicks-to-your-profile',
      'how-to-craft-a-linkedin-headline-that-attracts-clicks',
      'how-to-craft-a-linkedin-headline-that-drives-more-profile-views',
      'crafting-a-click-worthy-linkedin-headline-your-key-to-more-profile-views',
      'crafting-a-compelling-linkedin-headline-your-key-to-profile-clicks',
      'crafting-a-linkedin-headline-that-captivates-essential-tips',
      'crafting-a-linkedin-headline-that-attracts-attention-and-clicks',
    ],
  },
  {
    to: post('mastering-linkedin-networking-connect-naturally-and-confidently'),
    from: [
      'linkedin-networking-strategy-connect-without-the-awkwardness',
      'how-to-master-linkedin-networking-without-feeling-awkward',
      'mastering-linkedin-networking-connect-without-the-awkwardness',
    ],
  },
  {
    to: post('unlocking-hidden-job-opportunities-how-to-use-linkedin-effectively'),
    from: ['unlock-hidden-job-opportunities-mastering-linkedin-for-career-success'],
  },

  // ── Networking ───────────────────────────────────────────────
  {
    to: post('mastering-networking-at-conferences-tips-to-connect-without-feeling-awkward'),
    from: [
      'mastering-conference-networking-how-to-connect-without-feeling-awkward',
      'networking-at-conferences-master-the-art-without-feeling-awkward',
      'mastering-the-art-of-networking-at-conferences-practical-tips-for-success',
    ],
  },
  {
    to: post('b2b-networking-mastering-the-art-of-meaningful-business-connections'),
    from: ['b2b-networking-how-to-forge-meaningful-business-connections'],
  },

  // ── Digital business card ────────────────────────────────────
  {
    to: post('7-must-have-elements-for-a-memorable-digital-business-card'),
    from: ['7-essential-elements-every-digital-business-card-needs-to-stand-out'],
  },
  {
    to: post('mastering-digital-networking-sharing-your-digital-business-card-at-conferences-and-events'),
    from: [
      'mastering-the-art-of-sharing-your-digital-business-card-at-conferences',
      'mastering-the-art-of-sharing-your-digital-business-card-at-conferences-1',
    ],
  },

  // ── Freelancing ──────────────────────────────────────────────
  {
    to: post('how-to-land-your-first-freelance-client-without-experience'),
    from: [
      'how-to-land-your-first-freelance-client-without-experience-1',
      'how-to-land-your-first-freelance-client-with-zero-experience',
      'how-to-land-your-first-freelance-client-with-zero-experience-1',
      'how-to-secure-your-first-freelance-client-without-experience',
      'how-to-secure-your-first-freelance-client-with-zero-experience',
    ],
  },
  {
    to: post('how-to-create-the-perfect-freelancer-profile-that-attracts-clients'),
    from: ['how-to-craft-a-freelancer-profile-that-attracts-clients'],
  },
  {
    to: post('freelancer-business-card-essential-elements-and-tips-to-stand-out'),
    from: [
      'freelancer-business-card-essential-elements-and-tips-to-stand-out-1',
      'freelancer-business-card-essential-elements-and-standout-tips',
      'freelancer-business-card-essential-elements-and-how-to-stand-out',
      'freelancer-business-card-essential-elements-to-stand-out',
    ],
  },

  // ── Entrepreneurship & personal website ──────────────────────
  {
    to: post('how-to-build-a-professional-online-presence-for-your-small-business'),
    from: [
      'creating-a-professional-online-presence-for-your-small-business-a-comprehensive-guide',
      'elevate-your-small-business-crafting-a-professional-online-presence',
      'building-a-professional-online-presence-for-your-small-business',
      'how-to-create-a-professional-online-presence-for-your-small-business',
    ],
  },
  {
    to: post('how-to-leverage-your-personal-brand-to-attract-investors-and-partners'),
    from: [
      'harnessing-your-personal-brand-to-attract-investors-and-partners',
      'harnessing-your-personal-brand-to-attract-investors-and-partners-1',
      'harnessing-your-personal-brand-attracting-investors-and-partners',
      'harnessing-your-personal-brand-attracting-investors-and-partners-1',
    ],
  },
  {
    to: post('personal-branding-for-founders-how-to-become-the-face-of-your-startup'),
    from: [
      'personal-branding-for-founders-become-the-face-of-your-startup',
      'personal-branding-for-founders-become-the-face-of-your-startup-1',
    ],
  },
  {
    to: post('cover-letter-vs-no-cover-letter-do-employers-still-care'),
    from: ['cover-letter-vs-resume-whats-the-difference-and-why-you-need-both'],
  },
  {
    to: post('online-portfolio-vs-resume-what-recruiters-really-want'),
    from: [
      'online-portfolio-vs-resume-which-do-recruiters-prefer',
      'online-portfolio-vs-resume-which-do-recruiters-really-prefer',
    ],
  },
]

/** Old slug → target path. */
export const BLOG_REDIRECTS: Record<string, string> = Object.fromEntries(
  BLOG_REDIRECT_GROUPS.flatMap(group => group.from.map(slug => [slug, group.to])),
)

export const blogRedirectTarget = (slug: string): string | undefined => BLOG_REDIRECTS[slug]
